import { useMutation } from "@apollo/client";
import {
  CurrentUserOrganizationsDocument,
  UpdateUserDocument,
  UserInfosDocument,
} from "../../../../__generated__/graphql";
import toast from "react-hot-toast";
import { z } from "zod";
import { v4 as uuidv4 } from "uuid";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "../../../../components/Modal";
import FormInput from "../../../../components/FormInput";
import Button from "../../../../components/Button";
import { useUser } from "../../../../contexts/UserContext/hooks/useUser";
import useSignedUrlToGetFile from "../../../../hooks/useSignedUrlToGetFile";
import { FaCamera } from "react-icons/fa6";
import { useMemo, useState } from "react";
import { formatDateForInput } from "../../../../lib/utils";
import useSignedUrlToUploadFile from "../../../../hooks/useSignedUrlToUploadFile";

const updateUserSchema = z.object({
  email: z.string().email("Email invalide"),
  firstName: z.string().min(1, "Prénom requis"),
  lastName: z.string().min(1, "Nom requis"),
  phoneNumber: z.string().optional(),
  avatar: z.instanceof(File).optional(),
  jobTitle: z.string().optional(),
  birthDate: z
    .string()
    .transform((val) => (val === "" ? undefined : val))
    .optional(),
});

type UpdateUserFormData = z.infer<typeof updateUserSchema>;

export default function UpdateUserModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { userOrganization, userInfos } = useUser();
  const generatedBucketKey = useMemo(() => {
    return uuidv4();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const { data: signedUrlData } = useSignedUrlToGetFile(
    userOrganization?.avatarBucketKey
  );
  const { data: signedUrlDataToUpload } =
    useSignedUrlToUploadFile(generatedBucketKey);

  const [updateUser, { loading: isUpdating }] = useMutation(UpdateUserDocument);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    clearErrors,
  } = useForm<UpdateUserFormData>({
    defaultValues: {
      email: userInfos?.email || "",
      firstName: userInfos?.firstName || "",
      lastName: userInfos?.lastName || "",
      phoneNumber: userInfos?.phoneNumber || "",
      jobTitle: userInfos?.jobTitle || "",
      birthDate: userInfos?.birthDate
        ? formatDateForInput(userInfos?.birthDate)?.day
        : undefined,
    },
    resolver: zodResolver(updateUserSchema),
  });

  const onSubmit = async (data: UpdateUserFormData) => {
    try {
      if (avatarFile) {
        if (!signedUrlDataToUpload?.signedUrlToUploadFile) {
          return;
        }
        const response = await fetch(
          signedUrlDataToUpload.signedUrlToUploadFile,
          {
            method: "PUT",
            body: avatarFile,
          }
        );

        if (!response.ok) {
          throw new Error();
        }
      }

      await updateUser({
        variables: {
          updateUserInput: {
            ...data,
            avatarBucketKey: avatarFile
              ? generatedBucketKey
              : userOrganization?.avatarBucketKey,
          },
        },
        refetchQueries: [UserInfosDocument, CurrentUserOrganizationsDocument],
      });

      toast.success("Informations mises à jour avec succès");
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de la mise à jour des informations"
      );
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if the file is an image
    if (!file.type.startsWith("image/")) {
      toast.error("Seuls les fichiers images sont acceptés");
      return;
    }

    // Create preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setAvatarFile(file);
  };

  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    setAvatarFile(null);
    clearErrors();
    onClose();
  };

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={handleClose}
      title="Modifier mes informations"
      description="Modifier les informations de votre compte et faciliter la prise de contact avec vous"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex gap-8 w-xl">
        <div className="flex-col gap-4">
          <div className="text-sm font-medium mb-1">Photo de profil</div>
          {previewUrl || signedUrlData?.signedUrlToGetFile ? (
            <div>
              <img
                src={previewUrl || signedUrlData?.signedUrlToGetFile || ""}
                alt="Avatar"
                className="w-[200px]  object-cover rounded-md"
              />
              <label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
                <div className="text-sm text-tertiary cursor-pointer hover:text-primary/80 transition-colors duration-300 underline mt-1 text-center">
                  Modifier
                </div>
              </label>
            </div>
          ) : (
            <label>
              <div className="flex-col gap-4 w-[200px] h-[200px] bg-input rounded-md mx-auto flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity duration-300 border border-dashed border-border">
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
                <FaCamera className="w-10 h-10 text-tertiary" />
                <div className="text-sm text-tertiary">Changer</div>
              </div>
              {errors.avatar && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.avatar.message}
                </p>
              )}
            </label>
          )}
        </div>
        <div className="flex flex-col gap-4 w-full">
          <FormInput
            id="email"
            label="Email"
            error={errors.email}
            register={register}
          />
          <FormInput
            id="firstName"
            label="Prénom"
            error={errors.firstName}
            register={register}
          />
          <FormInput
            id="lastName"
            label="Nom"
            error={errors.lastName}
            register={register}
          />
          <FormInput
            id="phoneNumber"
            label="Téléphone"
            error={errors.phoneNumber}
            register={register}
          />
          <FormInput
            id="jobTitle"
            label="Fonction"
            error={errors.jobTitle}
            register={register}
          />
          <FormInput
            id="birthDate"
            label="Date de naissance"
            error={errors.birthDate}
            register={register}
            type="date"
          />
          <Button
            type="submit"
            isLoading={isUpdating}
            className="w-full"
            disabled={!isDirty && !avatarFile}
          >
            Enregistrer
          </Button>
        </div>
      </form>
    </Modal>
  );
}
